import { useAuth } from '@/hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { setWeatherForecast, setTimeConfig } from '@/lib/actions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, CloudSun, Wand2, Save, Clock, Paintbrush } from 'lucide-react';
import { TiempoAfectadas } from '@/components/jartinet/admin/TiempoAfectadas';
import {
  MINUTOS_SLOT, SLOTS_DIA, SLOTS_HORA, WEATHER_META as META, WEATHER_ORDER as ORDER,
  type CeldaTiempo, type Weather,
  diaDesdeFirestore, diaVacio, etiquetaSlot, formatCelda, madridDateStr,
} from '@/lib/tiempo';

function next14Days(): string[] {
  return Array.from({ length: 14 }, (_, i) => madridDateStr(new Date(Date.now() + i * 86400000)));
}
function prettyDay(dateStr: string) {
  const d = new Date(dateStr + 'T12:00:00');
  return new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: '2-digit', month: 'short' }).format(d);
}

/** Probabilidades del pincel: las de siempre a un clic, y el deslizador para el resto. */
const PROBS_RAPIDAS = [100, 80, 60, 40, 20];

/**
 * Genera un día "realista" de 96 casillas: mayormente despejado, con chubascos de
 * duración variable a los que se les da una probabilidad que **sube y baja**
 * (entra flojo, arrecia en medio y afloja al final). Es lo que hace que el
 * pronóstico se lea como un pronóstico y no como un interruptor.
 */
function genDay(prevRainy: boolean): CeldaTiempo[] {
  const dia = diaVacio().slice();
  const rainProb = prevRainy ? 0.5 : 0.28;
  if (Math.random() < rainProb) {
    const bloques = 1 + (Math.random() < 0.4 ? 1 : 0);
    for (let b = 0; b < bloques; b++) {
      // De 45 min a 5 h, en casillas de 15 min.
      const largo = 3 + Math.floor(Math.random() * 18);
      const inicio = Math.floor(Math.random() * (SLOTS_DIA - largo));
      const tormenta = Math.random() < 0.22;
      // Techo del chubasco: rara vez es una certeza.
      const pico = 45 + Math.floor(Math.random() * 55);
      for (let i = 0; i < largo; i++) {
        // Campana: 0 en los bordes, 1 en el centro del bloque.
        const t = largo === 1 ? 1 : Math.sin((Math.PI * i) / (largo - 1));
        const p = Math.max(10, Math.round(pico * (0.35 + 0.65 * t)));
        const w: Weather = tormenta && t > 0.6 ? 'thunder' : 'rain';
        dia[inicio + i] = { w, p };
      }
    }
  }
  return dia;
}

export default function AdminTiempoPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [days] = useState<string[]>(next14Days);
  const [forecast, setForecast] = useState<Record<string, CeldaTiempo[]>>({});
  const [realTimeDay, setRealTimeDay] = useState(true);
  const [saving, setSaving] = useState(false);

  // Pincel: qué se pinta al hacer clic (o al arrastrar) sobre la rejilla.
  const [brochaW, setBrochaW] = useState<Weather>('rain');
  const [brochaP, setBrochaP] = useState(70);
  const pintando = useRef(false);
  // Casilla bajo el ratón, para el lector de abajo (es donde se LEE el %).
  const [mirando, setMirando] = useState<{ date: string; slot: number } | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const u1 = onSnapshot(doc(db, 'mod_weather', 'main'), (snap) => {
      const f = (snap.exists() ? snap.data().forecast : {}) || {};
      // Solo la primera vez: si no, una escritura pisaría lo que se esté editando.
      setForecast((prev) => {
        if (Object.keys(prev).length) return prev;
        const out: Record<string, CeldaTiempo[]> = {};
        for (const [d, arr] of Object.entries(f)) out[d] = diaDesdeFirestore(arr);
        return out;
      });
    });
    const u2 = onSnapshot(doc(db, 'mod_time_config', 'main'), (snap) => {
      if (snap.exists() && typeof snap.data().realTimeDay === 'boolean') setRealTimeDay(snap.data().realTimeDay);
    });
    return () => { u1(); u2(); };
  }, [userData?.role]);

  // Soltar el ratón en cualquier sitio termina el trazo (si no, se seguiría
  // pintando al volver a entrar en la rejilla sin botón pulsado).
  useEffect(() => {
    const soltar = () => { pintando.current = false; };
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
  }, []);

  const dayData = (date: string): CeldaTiempo[] => forecast[date] || diaVacio();

  const brocha = (): CeldaTiempo =>
    (brochaW === 'clear' ? { w: 'clear', p: 0 } : { w: brochaW, p: brochaP });

  const pintar = (date: string, slot: number) => {
    setForecast((prev) => {
      const cur = (prev[date] || diaVacio()).slice();
      cur[slot] = brocha();
      return { ...prev, [date]: cur };
    });
  };

  const setWholeDay = (date: string, c: CeldaTiempo) => {
    setForecast((prev) => ({ ...prev, [date]: Array.from({ length: SLOTS_DIA }, () => c) }));
  };

  const generate = () => {
    const f: Record<string, CeldaTiempo[]> = {};
    let prevRainy = false;
    for (const d of days) {
      const day = genDay(prevRainy);
      f[d] = day;
      prevRainy = day.some((c) => c.w !== 'clear');
    }
    setForecast(f);
    toast({ title: 'Pronóstico generado', description: 'Revísalo y pulsa Guardar para aplicarlo.' });
  };

  const handleSave = async () => {
    setSaving(true);
    // Guarda solo los 14 días visibles (limpia días pasados), ya en texto.
    const trimmed: Record<string, string[]> = {};
    for (const d of days) trimmed[d] = dayData(d).map(formatCelda);
    const [r1, r2] = await Promise.all([setWeatherForecast(trimmed), setTimeConfig({ realTimeDay })]);
    if (r1.success && r2.success) {
      toast({ title: 'Guardado', description: `El servidor lo aplica cada ${MINUTOS_SLOT} minutos.` });
    } else {
      toast({ title: 'Error', description: r1.message || r2.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  if (loading || userData?.role !== 'admin') {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>;
  }

  const celdaMirada = mirando ? dayData(mirando.date)[mirando.slot] : null;

  return (
    <div className="space-y-6 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-sky-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="grid h-16 w-16 place-items-center rounded-2xl border border-sky-400/30 bg-sky-500/15 text-sky-300"><CloudSun className="h-9 w-9" /></div>
            <div>
              <h1 className="font-headline text-4xl font-bold md:text-5xl">Gestión de Tiempo</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">
                El día del servidor dura 24 h (sincronizado con la hora real). El clima cambia cada{' '}
                <strong className="text-foreground">{MINUTOS_SLOT} minutos</strong>, y la lluvia y la
                tormenta van con <strong className="text-foreground">probabilidad</strong>: un 60 % es
                un 60 %, no una certeza.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={generate} className="gap-2"><Wand2 className="h-4 w-4" /> Generar realista</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar</Button>
          </div>
        </div>
      </div>

      {/* Día de 24 h */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4">
        <Clock className="h-4 w-4 text-sky-400" />
        <span className="text-sm">Día de 24 h sincronizado con la hora real (Madrid)</span>
        <Switch checked={realTimeDay} onCheckedChange={setRealTimeDay} className="ml-auto" />
      </div>

      {/* PINCEL: lo que se pinta al hacer clic o arrastrar sobre la rejilla. */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          <Paintbrush className="h-4 w-4 text-primary" /> Pincel
        </span>
        <div className="flex gap-1">
          {ORDER.map((w) => {
            const I = META[w].icon;
            return (
              <Button
                key={w}
                size="sm"
                variant={brochaW === w ? 'default' : 'secondary'}
                onClick={() => setBrochaW(w)}
                className="gap-1.5"
              >
                <I className="h-3.5 w-3.5" style={{ color: brochaW === w ? undefined : META[w].color }} />
                {META[w].label}
              </Button>
            );
          })}
        </div>

        {brochaW !== 'clear' && (
          <>
            <span className="mx-1 h-5 w-px bg-white/10" />
            <span className="text-xs text-muted-foreground">Probabilidad</span>
            <input
              type="range" min={5} max={100} step={5} value={brochaP}
              onChange={(e) => setBrochaP(Number(e.target.value))}
              className="h-1.5 w-40 cursor-pointer accent-sky-400"
            />
            <span className="w-10 text-sm font-bold tabular-nums" style={{ color: META[brochaW].color }}>
              {brochaP}%
            </span>
            <div className="flex gap-1">
              {PROBS_RAPIDAS.map((p) => (
                <button
                  key={p}
                  onClick={() => setBrochaP(p)}
                  className={`rounded px-1.5 py-0.5 text-[0.65rem] font-bold transition-colors ${
                    brochaP === p ? 'bg-white/15 text-foreground' : 'text-muted-foreground hover:bg-white/8'}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </>
        )}

        {/* LECTOR: aquí es donde se ve el porcentaje exacto de una casilla. */}
        <span className="ml-auto min-w-[13rem] text-right text-xs">
          {mirando && celdaMirada ? (
            <>
              <span className="tabular-nums text-muted-foreground">{etiquetaSlot(mirando.slot)}</span>{' '}
              <strong style={{ color: META[celdaMirada.w].color }}>{META[celdaMirada.w].label}</strong>
              {celdaMirada.w !== 'clear' && (
                <strong className="ml-1 tabular-nums" style={{ color: META[celdaMirada.w].color }}>
                  {celdaMirada.p}%
                </strong>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">Pasa el ratón por una casilla para ver su %</span>
          )}
        </span>

        <p className="w-full text-[0.7rem] text-muted-foreground">
          Clic o <strong>arrastra</strong> sobre la rejilla para pintar. Cada casilla son{' '}
          {MINUTOS_SLOT} minutos y su <strong>opacidad es la probabilidad</strong>: cuanto más pálida,
          menos probable. El dado se tira <strong>una vez por hora</strong> y sale igual para el
          servidor y para todos los jugadores.
        </p>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap gap-3 text-xs">
        {ORDER.map((w) => {
          const I = META[w].icon;
          return <span key={w} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: META[w].color }} /><I className="h-3.5 w-3.5" /> {META[w].label}</span>;
        })}
      </div>

      {/* Rejilla a la izquierda; a la derecha, a quién le pilla el agua. */}
      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_400px]">
        {/* Rejilla 14 días × 96 casillas (15 min) */}
        <div className="space-y-1.5 overflow-x-auto pb-2">
          <div className="flex items-center gap-[3px] pl-[112px] text-[0.6rem] text-muted-foreground">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="w-[19px] text-center tabular-nums">{h % 2 === 0 ? h : ''}</div>
            ))}
          </div>
          {days.map((date) => {
            const data = dayData(date);
            const conAgua = data.filter((c) => c.w !== 'clear').length;
            return (
              <div key={date} className="flex items-center gap-[3px]" onPointerLeave={() => setMirando(null)}>
                <div className="w-[112px] shrink-0 text-xs">
                  <span className="font-medium capitalize">{prettyDay(date)}</span>
                  {conAgua > 0 && (
                    <span className="ml-1 text-[0.6rem] text-sky-300/70">
                      {Math.round((conAgua * MINUTOS_SLOT) / 60 * 10) / 10} h
                    </span>
                  )}
                </div>
                {/* Agrupadas por HORA: los huecos son lo que hace legible una tira de 96. */}
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="flex gap-px">
                    {Array.from({ length: SLOTS_HORA }, (_, i) => {
                      const slot = h * SLOTS_HORA + i;
                      const c = data[slot] || { w: 'clear' as Weather, p: 0 };
                      // La OPACIDAD es la probabilidad: así se ve sin leer nada.
                      const alpha = c.w === 'clear' ? 1 : 0.25 + 0.75 * (c.p / 100);
                      return (
                        <button
                          key={i}
                          title={`${etiquetaSlot(slot)} · ${META[c.w].label}${c.w === 'clear' ? '' : ` ${c.p}%`}`}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            pintando.current = true;
                            pintar(date, slot);
                          }}
                          onPointerEnter={() => {
                            setMirando({ date, slot });
                            if (pintando.current) pintar(date, slot);
                          }}
                          className="h-5 w-1 touch-none rounded-[1px] transition-opacity hover:opacity-70"
                          style={{ background: META[c.w].color, opacity: alpha }}
                        />
                      );
                    })}
                  </div>
                ))}
                <div className="ml-2 flex shrink-0 gap-1">
                  {ORDER.map((w) => {
                    const I = META[w].icon;
                    return (
                      <button
                        key={w}
                        title={`Todo el día: ${META[w].label}${w === 'clear' ? '' : ` ${brochaP}%`}`}
                        onClick={() => setWholeDay(date, w === 'clear' ? { w: 'clear', p: 0 } : { w, p: brochaP })}
                        className="rounded p-1 hover:bg-muted/40"
                      >
                        <I className="h-3.5 w-3.5" style={{ color: META[w].color }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Se le pasa el pronóstico EN MEMORIA (no el guardado): así la lista se
            recoloca al instante al tocar la rejilla. */}
        <TiempoAfectadas forecast={forecast} dias={days} />
      </div>
    </div>
  );
}
